import axios from 'axios'
import errorHandlerRest, { errorHandlerRestV2 } from './errorHandlerRest'
import { Cookie } from '@/mixin/local-cookie'

const axiosConfig = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
}

/*
 * NOTE:
 * apiClientV2 is created since we need to handle toast message from
 * parent completely, since apiClient (v1) is used in existing code,
 * removing the toast message will break the existing code.
 */
const apiClient = axios.create(axiosConfig)
export const apiClientV2 = axios.create(axiosConfig)

// TODO: Workaround
// When developing locally, the cookie 'resource' cookie is not available
// in the browser (httpOnly).
// So we need to manually copy the cookie after identity login
// and paste it to the localhost cookie
const localCookie = Cookie.getLocalCookie()

function setLocalCookie(config) {
  // If the local cookie is found (not httpOnly), then use it
  // if not, then use the httpOnly cookie, using withCredentials flag
  if (localCookie?.resource) {
    config.headers.Authorization = `Bearer ${localCookie?.resource}`
  } else {
    config.withCredentials = true
  }

  return config
}

/**
 * Check if URL needs proxy rewrite (ngrok, localhost, etc.)
 * 
 * @param {String} url 
 * @returns {Boolean}
 */
function isValidProxyUrl(url) {
  if (!url) return false
  // Only proxy URLs (ex: ngrok-free.app)
  return url.includes('ngrok-free.app')
}

/**
 * Converts a full API URL into its local proxy path.
 * 
 * During development, external APIs (like ngrok URLs) are proxied through the local dev server
 * to avoid CORS issues. This function maps each environment variable URL to its corresponding 
 * proxy path defined in vue.config.js.
 * 
 * @param {string} url - The full API URL from environment variables
 *                       Example: 'https://94e254b3aa46.ngrok-free.app/external/api/web/v1'
 * @returns {string} The local proxy path (e.g., '/proxy-order', '/proxy-fleet-planning')
 * @throws {Error} If the URL doesn't match any configured proxy
 * @example
 * getConfigProxyUrl(process.env.VUE_APP_API_ORDER_URL)
 * // returns '/proxy-order'
 */
function getConfigProxyUrl(url) {
  switch (url) {
    case process.env.VUE_APP_REST_API_VSMS:
      return '/proxy-vsms2-rest'
    case process.env.VUE_APP_REST_API_IDENTITY:
      return '/proxy-identity'
    case process.env.VUE_APP_API_ORDER_URL:
      return '/proxy-order'
    case process.env.VUE_APP_API_FINANCIAL_RECORD_URL:
      return '/proxy-financial-record'
    case process.env.VUE_APP_API_FLEET_PLANNING_URL:
      return '/proxy-fleet-planning'
    case process.env.VUE_APP_API_BFF_URL:
      return '/proxy-bff'
    case process.env.VUE_APP_API_VMS_URL:
      return '/proxy-vendor-management'
    case process.env.VUE_APP_REST_API_WEBHOOK:
      return '/proxy-webhook'
    default:
      throw new Error("Proxy not implemented yet");
  }
}

/**
 * Rewrites the baseURL to use local proxy paths during development.
 * This interceptor transforms external API URLs (like ngrok) into local proxy paths
 * that are configured in vue.config.js devServer proxy settings.
 * Only applies in development environment to avoid CORS issues.
 * 
 * @param {import("axios").InternalAxiosRequestConfig} config - Axios request configuration object
 * @returns {import("axios").InternalAxiosRequestConfig} Modified config with rewritten baseURL
 * @example
 * // Before: config.baseURL = 'https://abc123.ngrok.io/api/v1'
 * // After: config.baseURL = '/proxy-order'
 */
function handleProxyRewrite(config) {
  if (
    process.env.VUE_APP_IS_USING_PROXY === 'true' &&
    process.env.VUE_APP_ENV === 'development' && 
    config.baseURL && 
    isValidProxyUrl(config.baseURL)
  ) {
    config.baseURL = getConfigProxyUrl(config.baseURL)
  }
  return config
}

apiClient.interceptors.request.use(setLocalCookie)
apiClient.interceptors.request.use(handleProxyRewrite)
apiClientV2.interceptors.request.use(setLocalCookie)
apiClientV2.interceptors.request.use(handleProxyRewrite)

apiClient.interceptors.response.use((response) => response, errorHandlerRest)
apiClientV2.interceptors.response.use((response) => response, errorHandlerRestV2)

/**
 * @param {import("axios").AxiosRequestConfig} requestConfig
 */
export const defaultClient = async (requestConfig) => {
  const response = await apiClient({
    ...requestConfig,
  })

  return response?.data
}

/**
 * @param {import("axios").AxiosRequestConfig} requestConfig
 */
export const defaultClientV2 = async (requestConfig) => {
  const response = await apiClientV2({
    ...requestConfig,
  })

  return response?.data
}

export default apiClient

// Development warning for proxy usage with ngrok
/* eslint-disable no-console */
if (process.env.VUE_APP_IS_USING_PROXY === 'true' && process.env.VUE_APP_ENV === 'development') {
  const proxyUrls = [
    process.env.VUE_APP_REST_API_VSMS,
    process.env.VUE_APP_REST_API_IDENTITY,
    process.env.VUE_APP_API_ORDER_URL,
    process.env.VUE_APP_API_FINANCIAL_RECORD_URL,
    process.env.VUE_APP_API_FLEET_PLANNING_URL,
    process.env.VUE_APP_API_BFF_URL,
    process.env.VUE_APP_API_VMS_URL,
    process.env.VUE_APP_REST_API_WEBHOOK,
  ].filter(url => url && isValidProxyUrl(url))

  if (proxyUrls.length > 0 && !localCookie?.resource) {
    console.error('🚨 REST API Proxy Error: No authentication token found!')
    console.error('📋 To use proxy with authentication:')
    console.error('   1. Login on your development site (e.g., .mceasy.cloud)')
    console.error('   2. Open Application tab and go to Cookies')
    console.error('   3. Find cookie with name "resource" and copy its value')
    console.error('   4. Create new cookie on localhost domain with name "resource" and paste the value')
    console.error('   5. Refresh localhost to apply the token')
  }
}
/* eslint-enable no-console */
